import { useEffect, useId, useRef, useState } from 'react';

import { cx } from '../../lib/cx';
import type { MenuProps } from './types';

/**
 * A dropdown of actions: opens on click or arrow down, arrows move, Enter selects, Escape and
 * outside clicks close, focus returns to the trigger.
 */
export const Menu = ({ trigger, label, items }: MenuProps) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onDocumentClick = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')[active]?.focus();
    }
  }, [open, active]);

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const enabled = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.disabled);
    const position = enabled.findIndex(({ index }) => index === active);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(enabled[(position + 1) % enabled.length]?.index ?? 0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(enabled[(position - 1 + enabled.length) % enabled.length]?.index ?? 0);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div ref={root} className="relative inline-block">
      <button
        ref={button}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={label}
        onClick={() => {
          setActive(items.findIndex((item) => !item.disabled));
          setOpen((value) => !value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive(items.findIndex((item) => !item.disabled));
            setOpen(true);
          }
        }}
        className="control-touch inline-flex items-center gap-1 rounded-control px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
      >
        {trigger}
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onKeyDown}
          className="absolute right-0 z-40 mt-1 min-w-44 rounded-card border border-gray-200 bg-white py-1 shadow-overlay"
        >
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              tabIndex={index === active ? 0 : -1}
              disabled={item.disabled}
              onClick={() => {
                close();
                item.onSelect();
              }}
              className={cx(
                'block w-full px-3 py-2 text-left text-sm focus:outline-none focus:bg-gray-100 disabled:opacity-50',
                item.danger ? 'text-danger-700' : 'text-gray-700',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
